import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { authService } from "../services";
import { authSessionStore } from "./auth-session.store";

class AuthFormsStore {
  loading = false;
  isRegister = false;

  loginForm = {
    serverError: null,
  };

  registerForm = {
    serverError: null,
  };

  forgotStep = "email"; // 'email' | 'otp'
  forgotEmail = "";
  forgotOtp = ["", "", "", "", "", ""];
  forgotRawToken = null;
  forgotOtpToken = null;
  forgotNewPassword = "";
  forgotConfirmPassword = "";
  forgotReset = {
    serverError: null,
    errors: {},
  };

  email2FAOtp = ["", "", "", "", "", ""];
  email2FAOtpToken = null;
  verifyEmail2FAResult = {
    serverError: null,
    errors: {},
  };
  sendEmail2FAResult = {
    serverError: null,
    errors: {},
  };

  changePasswordForm = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    serverError: null,
    errors: {},
  };

  constructor() {
    makeAutoObservable(this);
  }

  setLoading = (val) => {
    this.loading = Boolean(val);
  };

  toggleRegister = (next) => {
    this.isRegister = typeof next === "boolean" ? next : !this.isRegister;
  };

  clearLoginError = () => {
    this.loginForm.serverError = null;
  };

  clearRegisterError = () => {
    this.registerForm.serverError = null;
  };

  login = async (payload, options = {}) => {
    runInAction(() => {
      this.loading = true;
      this.clearLoginError();
    });

    try {
      const { onValidationError, ...requestOptions } = options;
      const loginPromise = authService.login(payload, {
        ...requestOptions,
        onValidationError: (errors) => {
          runInAction(() => {
            this.loginForm.serverError = errors;
          });
          onValidationError?.(errors);
        },
      });

      const data = await loginPromise;
      const { user, accessToken, refreshTokenExpiresAt } = data || {};

      if (user?.email2FAEnabled) {
        return data;
      }
      showToast.promise(
        Promise.resolve(data),
        {
          loading: "Logging in...",
          success: "Logged in successfully!",
          error: (err) => err.message,
        },
        { minDelay: 500 },
      );
      runInAction(() => {
        authSessionStore.applyAuthSuccess({
          user,
          accessToken,
          refreshTokenExpiresAt,
        });
      });
      return data;
    } catch (error) {
      showToast.error(error.message);
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  register = async (payload) => {
    runInAction(() => {
      this.loading = true;
      this.clearRegisterError();
    });

    try {
      const registerPromise = authService.register(payload, {
        onValidationError: (errors) => {
          runInAction(() => {
            this.registerForm.serverError = errors;
          });
        },
      });

      showToast.promise(registerPromise, {
        loading: "Registering...",
        success: "Registered successfully!",
        error: (err) => err.message,
      });

      const data = await registerPromise;
      const { user, accessToken, refreshTokenExpiresAt } = data || {};
      runInAction(() => {
        authSessionStore.applyAuthSuccess({
          user,
          accessToken,
          refreshTokenExpiresAt,
        });
      });
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  // Forgot password setters
  setForgotStep = (step) => {
    this.forgotStep = step;
  };

  setForgotEmail = (email) => {
    this.forgotEmail = email;
  };

  setForgotOtpAt = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      this.forgotOtp[index] = value;
    }
  };

  setEmail2FAOtpAt = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      this.email2FAOtp[index] = value;
    }
  };

  setForgotNewPassword = (password) => {
    this.forgotNewPassword = password;
  };

  setForgotConfirmPassword = (password) => {
    this.forgotConfirmPassword = password;
  };

  setTokenFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      this.forgotRawToken = token;
    }
  };

  clearForgotPasswordState = () => {
    this.forgotStep = "email";
    this.forgotEmail = "";
    this.forgotOtp = ["", "", "", "", "", ""];
    this.forgotRawToken = null;
    this.forgotOtpToken = null;
    this.forgotNewPassword = "";
    this.forgotConfirmPassword = "";
    this.forgotReset = { serverError: null, errors: {} };
  };

  getForgotResetError = (field) => {
    return this.forgotReset.errors?.[field];
  };

  // Forgot password actions
  requestPasswordReset = async () => {
    runInAction(() => {
      this.loading = true;
      this.forgotReset.serverError = null;
    });

    try {
      const resetPromise = authService.requestPasswordReset(
        { email: this.forgotEmail },
        {
          onValidationError: (errors) => {
            runInAction(() => {
              this.forgotReset.serverError = errors;
            });
          },
        },
      );

      showToast.promise(resetPromise, {
        loading: "Sending reset code...",
        success: "Reset code sent to your email!",
        error: (err) => err.message,
      });

      const data = await resetPromise;
      runInAction(() => {
        this.forgotStep = "otp";
      });
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  verifyOtp = async () => {
    runInAction(() => {
      this.loading = true;
      this.forgotReset.serverError = null;
    });

    try {
      const otpCode = this.forgotOtp.join("");
      const verifyPromise = authService.verifyOtp(
        { email: this.forgotEmail, otp: otpCode },
        {
          onValidationError: (errors) => {
            runInAction(() => {
              this.forgotReset.serverError = errors;
            });
          },
        },
      );

      showToast.promise(verifyPromise, {
        loading: "Verifying code...",
        success: "Code verified!",
        error: (err) => err.message,
      });

      const data = await verifyPromise;
      runInAction(() => {
        this.forgotOtpToken = data?.otpToken;
      });
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  confirmResetToken = async () => {
    runInAction(() => {
      this.loading = true;
    });

    try {
      const data = await authService.confirmResetToken(
        { token: this.forgotRawToken },
        { throwOnError: false },
      );
      if (data) {
        runInAction(() => {
          this.forgotOtpToken = data?.token || this.forgotRawToken;
        });
      }
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  submitNewPassword = async (values) => {
    runInAction(() => {
      this.loading = true;
      this.forgotReset.serverError = null;
      this.forgotReset.errors = {};
    });

    try {
      const newPassword = values?.password ?? this.forgotNewPassword;
      const confirmPassword =
        values?.confirmPassword ?? this.forgotConfirmPassword;
      const config = this.forgotOtpToken
        ? { headers: { Authorization: `Bearer ${this.forgotOtpToken}` } }
        : {};

      const resetPromise = authService.updatePassword(
        {
          newPassword,
          confirmPassword,
        },
        {
          ...config,
          onValidationError: (errors) => {
            runInAction(() => {
              if (typeof errors === "object" && !Array.isArray(errors)) {
                this.forgotReset.errors = errors;
              } else {
                this.forgotReset.serverError = errors;
              }
            });
          },
        },
      );

      showToast.promise(resetPromise, {
        loading: "Updating password...",
        success: "Password updated successfully!",
        error: (err) => err.message,
      });

      const data = await resetPromise;
      runInAction(() => {
        this.clearForgotPasswordState();
      });
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  // Change password (for logged-in users)
  setChangePasswordField = (field, value) => {
    this.changePasswordForm[field] = value;
  };

  clearChangePasswordForm = () => {
    this.changePasswordForm = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      serverError: null,
      errors: {},
    };
  };

  getChangePasswordError = (field) => {
    return this.changePasswordForm.errors?.[field];
  };

  changePassword = async (values) => {
    runInAction(() => {
      this.loading = true;
      this.changePasswordForm.serverError = null;
      this.changePasswordForm.errors = {};
    });

    try {
      const changePromise = authService.updatePassword(
        {
          oldPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        },
        {
          onValidationError: (errors) => {
            runInAction(() => {
              if (typeof errors === "object" && !Array.isArray(errors)) {
                this.changePasswordForm.errors = errors;
              } else {
                this.changePasswordForm.serverError = errors;
              }
            });
          },
        },
      );

      showToast.promise(changePromise, {
        loading: "Changing password...",
        success: "Password changed successfully!",
        error: (err) => err.message,
      });

      const data = await changePromise;
      runInAction(() => {
        this.clearChangePasswordForm();
      });
      // Apply updated user/tokens so isNew becomes false in session store
      if (data?.user) {
        authSessionStore.applyAuthSuccess({
          user: data.user,
          accessToken: data.accessToken,
          refreshTokenExpiresAt: data.refreshTokenExpiresAt,
        });
      }
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  //Email 2FA
  sendEmail2FACode = async (email) => {
    runInAction(() => {
      this.loading = true;
      this.sendEmail2FAResult.serverError = null;
    });
    try {
      const send2FAPromise = authService.send2FACode(
        { email },
        {
          onValidationError: (errors) => {
            runInAction(() => {
              this.sendEmail2FAResult.serverError = errors;
            });
          },
        },
      );
      showToast.promise(send2FAPromise, {
        loading: "Sending 2FA code...",
        success: "2FA code sent to your email!",
        error: (err) => err.message,
      });
      const data = await send2FAPromise;
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  verifyEmail2FAOtp = async (email) => {
    runInAction(() => {
      this.loading = true;
      this.verifyEmail2FAResult.serverError = null;
    });

    try {
      const otpCode = this.email2FAOtp.join("");
      const verifyPromise = authService.verify2FAOtp(
        { email, otp: otpCode },
        {
          onValidationError: (errors) => {
            runInAction(() => {
              this.verifyEmail2FAResult.serverError = errors;
            });
          },
        },
      );

      showToast.promise(verifyPromise, {
        loading: "Verifying 2FA code...",
        success: "Email 2FA Code verified!",
        error: (err) => err.message,
      });

      const data = await verifyPromise;
      const { user, accessToken, refreshTokenExpiresAt } = data || {};
      runInAction(() => {
        if (accessToken) {
          authSessionStore.applyAuthSuccess({
            user,
            accessToken,
            refreshTokenExpiresAt,
          });
        }
      });
      return data;
    } catch (error) {
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };
}

export const authFormsStore = new AuthFormsStore();
